import { Component, OnDestroy, OnInit } from '@angular/core';
import { HubRecordEditorBase } from '../../_hub_record_editor_base';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { getCurrentFiscalYear } from '../../../../shared/helpers/get-current-fiscal-year';
import { ProjectFinancialModel, ProjectModel, UserModel } from '../../../../../hub_schema/hubTypes';

const estimatedTncCostsFinanceMetricId: number = 1000;
const estimatedNonTncCostsFinanceMetricId: number = 1001;

@Component({
    selector: 'app-estimated-financials-panel',
    templateUrl: './estimated-financials-panel.component.html',
    styleUrl: './estimated-financials-panel.component.scss',
    standalone: false
})
export class EstimatedFinancialsPanelComponent extends HubRecordEditorBase implements OnInit, OnDestroy {
    public fiscalYears: number[];
    public financialsEditForm: FormGroup;
    private valueChangeSubscriptions: Subscription[] = [];
    private financialValiditySubscription: Subscription;

    public get fiscalYearArray(): FormArray {
        return this.financialsEditForm.controls.fiscalYears as FormArray;
    }

    public ngOnInit(): void {
        this.fiscalYears = this.getFiscalYears();
        this.createfinancialsEditForm();

        this.hubRecordEditService.projectUpdated.subscribe((savedProject: ProjectModel) => {
            this.createfinancialsEditForm();
        });

    }

    public getFiscalYears(): number[] {
        // start with first fiscal year in the financials

        const fiscalYears = this.project.projectFinancials?.map(pf => pf.year).sort();
        const years: number[] = [];
        let year = fiscalYears[0];

        if (!year) {
            year = getCurrentFiscalYear();
        }

        while (year <= 2030) {
            years.push(year);
            year++;
        }
        return years;
    }

    public getFiscalYear(fg: FormGroup): number {
        return (fg.controls.estimatedTncCosts as FormGroup).controls.year.value;
    }

    public getEstimatedTncCostsUpdatedBy(fg: FormGroup): string {
        const control = (fg.controls.estimatedTncCosts as FormGroup).controls.updatedBy;

        if (!control || !control.value) {
            return '';
        }
        return control.value;
    }

    public getEstimatedTncCostsUpdatedOn(fg: FormGroup): Date {
        return (fg.controls.estimatedTncCosts as FormGroup).controls.updatedOn.value;
    }

    public getEstimatedNonTncCostsUpdatedBy(fg: FormGroup): string {
        const control = (fg.controls.estimatedNonTncCosts as FormGroup).controls.updatedBy;

        if (!control || !control.value) {
            return '';
        }
        return control.value;
    }

    public getEstimatedNonTncCostsUpdatedOn(fg: FormGroup): Date {
        return (fg.controls.estimatedNonTncCosts as FormGroup).controls.updatedOn.value;
    }

    private createfinancialsEditForm(): void {
        this.financialsEditForm = new FormGroup({
            fiscalYears: new FormArray([])
        });

        for (let fiscalYear of this.fiscalYears) {
            const yearFinancials = this.project.projectFinancials!.filter(f => f.year == fiscalYear);
            const estimatedTncCosts = yearFinancials?.find(f => f.financeMetricId === estimatedTncCostsFinanceMetricId);
            const estimatedNonTncCosts = yearFinancials?.find(f => f.financeMetricId === estimatedNonTncCostsFinanceMetricId);

            const formGroup = new FormGroup({
                'estimatedTncCosts': new FormGroup({
                    'projectFinancialId': new FormControl(estimatedTncCosts ? estimatedTncCosts.projectFinancialId : null),
                    'financeMetricId': new FormControl(estimatedTncCostsFinanceMetricId),
                    'year': new FormControl(fiscalYear),
                    'financeValue': new FormControl(estimatedTncCosts ? estimatedTncCosts.value : 0, {
                        updateOn: 'blur',
                        validators: [Validators.required, Validators.pattern("^[0-9]*$")]
                    }),
                    'updatedOn': new FormControl(estimatedTncCosts?.modifiedOn ? estimatedTncCosts.modifiedOn : null),
                    'updatedBy': new FormControl(estimatedTncCosts?.modifiedBy ? estimatedTncCosts.modifiedBy : null)
                }),
                'estimatedNonTncCosts': new FormGroup({
                    'projectFinancialId': new FormControl(estimatedNonTncCosts ? estimatedNonTncCosts.projectFinancialId : null),
                    'financeMetricId': new FormControl(estimatedNonTncCostsFinanceMetricId),
                    'year': new FormControl(fiscalYear),
                    'financeValue': new FormControl(estimatedNonTncCosts ? estimatedNonTncCosts.value : 0, {
                        updateOn: 'blur',
                        validators: [Validators.required, Validators.pattern("^[0-9]*$")]
                    }),
                    'updatedOn': new FormControl(estimatedNonTncCosts?.modifiedOn ? estimatedNonTncCosts.modifiedOn : null),
                    'updatedBy': new FormControl(estimatedNonTncCosts?.modifiedBy ? estimatedNonTncCosts.modifiedBy : null)
                })
            });

            // Keep this form synchronized with projectEditForm.projectFinancials
            const tncCostsSubscription = (formGroup.controls.estimatedTncCosts as FormGroup).controls.financeValue.valueChanges.subscribe((value: number) => {
                this.estimatedTncCostChanged(fiscalYear, value)
            });

            // Keep this form synchronized with projectEditForm.projectFinancials
            const nonTncCostsSubscription = (formGroup.controls.estimatedNonTncCosts as FormGroup).controls.financeValue.valueChanges.subscribe((value: number) => {
                this.estimatedNonTncCostChanged(fiscalYear, value);
            });

            this.valueChangeSubscriptions.push(tncCostsSubscription);
            this.valueChangeSubscriptions.push(nonTncCostsSubscription);

            (this.financialsEditForm.controls.fiscalYears as FormArray).push(formGroup);
        }

        this.financialValiditySubscription = this.financialsEditForm.statusChanges.subscribe((status: string) => {
            this.hubRecordEditService.isFinancialEditPanelValid = status === 'VALID';
        });
    }

    private estimatedTncCostChanged(fiscalYear: number, financeValue: number) {
        const allProjectFinancials = this.projectEditForm?.controls.projectFinancials.value as ProjectFinancialModel[];
        let projectFinancial = allProjectFinancials.find(f => f.year === fiscalYear && f.financeMetricId === estimatedTncCostsFinanceMetricId);

        if (projectFinancial) {
            projectFinancial.value = financeValue;
        }
        else {
            projectFinancial = {
                projectId: this.project.projectId!,
                year: fiscalYear,
                financeMetricId: estimatedTncCostsFinanceMetricId,
                value: financeValue
            };
            allProjectFinancials.push(projectFinancial);
        }
        this.projectEditForm?.controls.projectFinancials.markAsDirty();
    }

    private estimatedNonTncCostChanged(fiscalYear: number, financeValue: number) {
        const allProjectFinancials = this.projectEditForm?.controls.projectFinancials.value as ProjectFinancialModel[];
        let projectFinancial = allProjectFinancials.find(f => f.year === fiscalYear && f.financeMetricId === estimatedNonTncCostsFinanceMetricId);

        if (projectFinancial) {
            projectFinancial.value = financeValue;
        }
        else {
            projectFinancial = {
                projectId: this.project.projectId!,
                year: fiscalYear,
                financeMetricId: estimatedNonTncCostsFinanceMetricId,
                value: financeValue
            };
            allProjectFinancials.push(projectFinancial);
        }
        this.projectEditForm?.controls.projectFinancials.markAsDirty();
    }

    public getTotalEstimatedTncCosts(): number {
        let sum = 0;

        for (const fiscalYearGroup of this.fiscalYearArray.controls) {
            const estimatedTncCostGroup = (fiscalYearGroup as FormGroup).controls.estimatedTncCosts as FormGroup;
            sum += estimatedTncCostGroup.controls.financeValue.value;
        }

        return sum;
    }

    public getTotalEstimatedNonTncCosts(): number {
        let sum = 0;

        for (const fiscalYearGroup of this.fiscalYearArray.controls) {
            const estimatedTncCostGroup = (fiscalYearGroup as FormGroup).controls.estimatedNonTncCosts as FormGroup;
            sum += estimatedTncCostGroup.controls.financeValue.value;
        }

        return sum;
    }

    public ngOnDestroy(): void {
        if (this.financialValiditySubscription) {
            this.financialValiditySubscription.unsubscribe();
        }
        this.valueChangeSubscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
    }

}
